import { useState } from 'react';
import ConsultationModal from './ConsultationModal';

interface PlanFeature {
  text: string;
  bold?: boolean;
  shield?: boolean;
}

interface PricingPlan {
  category: string;
  name: string;
  price: string;
  priceUnit: string;
  features: PlanFeature[];
  note?: string;
  highlighted: boolean;
  badge?: string;
  ctaLabel: string;
}

const plans: PricingPlan[] = [
  {
    category: '系統導入',
    name: 'O2O 數位引擎模組導入',
    price: 'NT$ 48,000',
    priceUnit: '單次系統架構費',
    features: [
      { text: '免登入品牌展示空間 (展間)' },
      { text: '員工權限登入 (User Profiles)' },
      { text: '防呆業績回報模組' },
      { text: '追蹤代碼埋設工程 (GTM/Pixel)' },
    ],
    note: '需另計每月 NT$ 3,000 技術保固費覆蓋雲端主機成本',
    highlighted: false,
    ctaLabel: '預約方案諮詢',
  },
  {
    category: '基礎精準獲客',
    name: '方案 A | 精準再行銷代操',
    price: 'NT$ 15,000',
    priceUnit: '/ 月',
    features: [
      { text: '每月潛在受眾打包投放' },
      { text: '針對「高意願未留單」客群精準再行銷' },
      { text: '基礎成效報表' },
      { text: '(備註：廣告素材與文案由企業方提供)' },
    ],
    highlighted: false,
    ctaLabel: '預約方案諮詢',
  },
  {
    category: '企業護城河',
    name: '方案 B | 頂級合規全效獲客',
    price: 'NT$ 25,000',
    priceUnit: '/ 月',
    features: [
      { text: '包含方案 A 所有服務', bold: true },
      { text: '🛡️ 廣告合規與平台政策把關 (大幅降低停權風險)', shield: true },
      { text: '消費者保護規範健檢與合規建議' },
      { text: '進階戰略報表與轉換率優化建議' },
    ],
    highlighted: true,
    badge: '🔥 最受推薦',
    ctaLabel: '預約方案諮詢',
  },
];

interface PricingSectionProps {
  onCTAClick?: () => void;
}

export default function PricingSection({ onCTAClick }: PricingSectionProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCTA = () => {
    setIsModalOpen(true);
  };

  return (
    <>
      <section id="pricing" className="relative py-24 bg-[#060F1E] overflow-hidden">
        {/* 背景裝飾 */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#D4AF37]/5 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#00D4FF]/5 rounded-full blur-[100px]"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* 標題區 */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-5 py-2 mb-6">
              <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase">Pricing &amp; Solutions</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5">
              數據獲客方案
              <span className="block mt-1 bg-gradient-to-r from-[#D4AF37] to-[#F0C040] bg-clip-text text-transparent">
                企業級方案比較
              </span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
              從數位引擎模組導入到全鏈路行銷佈局，為您的企業打造<strong className="text-white">全自動獲客印鈔漏斗</strong>。
            </p>
          </div>

          {/* ── 比較表格區塊 ── */}
          <div className="mb-20">
            {/* 比較表標題 */}
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
                頂級企業主，為什麼最終都選擇
                <span className="bg-gradient-to-r from-[#D4AF37] to-[#F0C040] bg-clip-text text-transparent">「全鏈獲客」</span>？
              </h2>
            </div>

            {/* 比較表格 */}
            <div className="overflow-x-auto rounded-2xl border border-white/10">
              <table className="w-full min-w-[640px] border-collapse">
                {/* 表頭 */}
                <thead>
                  <tr>
                    <th className="bg-[#0A1628] px-6 py-5 text-left text-xs font-semibold tracking-widest text-gray-400 uppercase border-b border-white/10 w-[26%]">
                      比較維度
                    </th>
                    <th className="bg-[#0A1628] px-6 py-5 text-center text-sm font-bold text-red-400 border-b border-white/10 border-l border-white/10 w-[37%]">
                      <span className="flex items-center justify-center gap-2">
                        <span className="text-base">❌</span>
                        <span>傳統架站包商</span>
                      </span>
                      <span className="block text-xs font-normal text-gray-500 mt-1 tracking-wide">沉沒成本</span>
                    </th>
                    <th className="bg-gradient-to-r from-[#0D1F35] to-[#0A1A30] px-6 py-5 text-center text-sm font-bold text-[#00D4FF] border-b border-[#00D4FF]/30 border-l border-white/10 w-[37%]">
                      <span className="flex items-center justify-center gap-2">
                        <span className="text-base">🛡️</span>
                        <span>全鏈獲客整合行銷</span>
                      </span>
                      <span className="block text-xs font-normal text-[#00D4FF]/60 mt-1 tracking-wide">營收資產</span>
                    </th>
                  </tr>
                </thead>

                {/* 表身 */}
                <tbody>
                  {/* Row 1 - 視覺與素材 */}
                  <tr className="group">
                    <td className="bg-[#080F1C] px-6 py-5 border-b border-white/8 border-r border-white/8">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 flex items-center justify-center rounded-md bg-white/5 flex-shrink-0">
                          <i className="ri-image-line text-gray-400 text-sm"></i>
                        </div>
                        <span className="text-white font-semibold text-sm">視覺與素材</span>
                      </div>
                    </td>
                    <td className="bg-[#0D0A0A] px-6 py-5 border-b border-white/8 border-r border-white/8 text-center">
                      <div className="flex items-start justify-center gap-2">
                        <span className="text-red-500 text-xs mt-0.5 flex-shrink-0">✕</span>
                        <span className="text-gray-400 text-sm leading-relaxed">依賴公版圖庫 / 視覺單調</span>
                      </div>
                    </td>
                    <td className="bg-gradient-to-r from-[#071525] to-[#060F1E] px-6 py-5 border-b border-[#00D4FF]/10 text-center relative">
                      <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#00D4FF]/40 via-[#00D4FF]/20 to-transparent"></div>
                      <div className="flex items-start justify-center gap-2">
                        <span className="text-[#00D4FF] text-xs mt-0.5 flex-shrink-0">✓</span>
                        <span className="text-white text-sm leading-relaxed font-medium">專人實拍形象影片 / 視覺鉤子強</span>
                      </div>
                    </td>
                  </tr>

                  {/* Row 2 - 漏斗設計 */}
                  <tr className="group">
                    <td className="bg-[#080F1C] px-6 py-5 border-b border-white/8 border-r border-white/8">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 flex items-center justify-center rounded-md bg-white/5 flex-shrink-0">
                          <i className="ri-filter-line text-gray-400 text-sm"></i>
                        </div>
                        <span className="text-white font-semibold text-sm">漏斗設計</span>
                      </div>
                    </td>
                    <td className="bg-[#0D0A0A] px-6 py-5 border-b border-white/8 border-r border-white/8 text-center">
                      <div className="flex items-start justify-center gap-2">
                        <span className="text-red-500 text-xs mt-0.5 flex-shrink-0">✕</span>
                        <span className="text-gray-400 text-sm leading-relaxed">無動線規劃 / 流量進站即流失</span>
                      </div>
                    </td>
                    <td className="bg-gradient-to-r from-[#071525] to-[#060F1E] px-6 py-5 border-b border-[#00D4FF]/10 text-center relative">
                      <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#00D4FF]/40 via-[#00D4FF]/20 to-transparent"></div>
                      <div className="flex items-start justify-center gap-2">
                        <span className="text-[#00D4FF] text-xs mt-0.5 flex-shrink-0">✓</span>
                        <span className="text-white text-sm leading-relaxed font-medium">量身打造單頁漏斗 / 創造極高轉換率</span>
                      </div>
                    </td>
                  </tr>

                  {/* Row 3 - 風險把關 */}
                  <tr className="group">
                    <td className="bg-[#080F1C] px-6 py-5 border-b border-white/8 border-r border-white/8">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 flex items-center justify-center rounded-md bg-white/5 flex-shrink-0">
                          <i className="ri-shield-line text-gray-400 text-sm"></i>
                        </div>
                        <span className="text-white font-semibold text-sm">風險把關</span>
                      </div>
                    </td>
                    <td className="bg-[#0D0A0A] px-6 py-5 border-b border-white/8 border-r border-white/8 text-center">
                      <div className="flex items-start justify-center gap-2">
                        <span className="text-red-500 text-xs mt-0.5 flex-shrink-0">✕</span>
                        <span className="text-gray-400 text-sm leading-relaxed">文案盲目上架 / 易遭平台停權</span>
                      </div>
                    </td>
                    <td className="bg-gradient-to-r from-[#071525] to-[#060F1E] px-6 py-5 border-b border-[#00D4FF]/10 text-center relative">
                      <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#00D4FF]/40 via-[#00D4FF]/20 to-transparent"></div>
                      <div className="flex items-start justify-center gap-2">
                        <span className="text-[#00D4FF] text-xs mt-0.5 flex-shrink-0">✓</span>
                        <span className="text-white text-sm leading-relaxed font-medium">顧問審視合規建議 / 大幅排雷防封號</span>
                      </div>
                    </td>
                  </tr>

                  {/* Row 4 - 數據追蹤 */}
                  <tr className="group">
                    <td className="bg-[#080F1C] px-6 py-5 border-b border-white/8 border-r border-white/8">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 flex items-center justify-center rounded-md bg-white/5 flex-shrink-0">
                          <i className="ri-bar-chart-line text-gray-400 text-sm"></i>
                        </div>
                        <span className="text-white font-semibold text-sm">數據追蹤</span>
                      </div>
                    </td>
                    <td className="bg-[#0D0A0A] px-6 py-5 border-b border-white/8 border-r border-white/8 text-center">
                      <div className="flex items-start justify-center gap-2">
                        <span className="text-red-500 text-xs mt-0.5 flex-shrink-0">✕</span>
                        <span className="text-gray-400 text-sm leading-relaxed">被動等客 / 無法追蹤轉換來源</span>
                      </div>
                    </td>
                    <td className="bg-gradient-to-r from-[#071525] to-[#060F1E] px-6 py-5 border-b border-[#00D4FF]/10 text-center relative">
                      <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#00D4FF]/40 via-[#00D4FF]/20 to-transparent"></div>
                      <div className="flex items-start justify-center gap-2">
                        <span className="text-[#00D4FF] text-xs mt-0.5 flex-shrink-0">✓</span>
                        <span className="text-white text-sm leading-relaxed font-medium">深度埋設 AI 追蹤 (GTM/CAPI)</span>
                      </div>
                    </td>
                  </tr>

                  {/* Row 5 - 名單管理 */}
                  <tr className="group">
                    <td className="bg-[#080F1C] px-6 py-5 border-b border-white/8 border-r border-white/8">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 flex items-center justify-center rounded-md bg-white/5 flex-shrink-0">
                          <i className="ri-contacts-line text-gray-400 text-sm"></i>
                        </div>
                        <span className="text-white font-semibold text-sm">名單管理</span>
                      </div>
                    </td>
                    <td className="bg-[#0D0A0A] px-6 py-5 border-b border-white/8 border-r border-white/8 text-center">
                      <div className="flex items-start justify-center gap-2">
                        <span className="text-red-500 text-xs mt-0.5 flex-shrink-0">✕</span>
                        <span className="text-gray-400 text-sm leading-relaxed">名單散落各處 / 業績口頭回報</span>
                      </div>
                    </td>
                    <td className="bg-gradient-to-r from-[#071525] to-[#060F1E] px-6 py-5 border-b border-[#00D4FF]/10 text-center relative">
                      <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#00D4FF]/40 via-[#00D4FF]/20 to-transparent"></div>
                      <div className="flex items-start justify-center gap-2">
                        <span className="text-[#00D4FF] text-xs mt-0.5 flex-shrink-0">✓</span>
                        <span className="text-white text-sm leading-relaxed font-medium">O2O 系統集中管理 / 防呆業績追蹤</span>
                      </div>
                    </td>
                  </tr>

                  {/* 總結行 */}
                  <tr>
                    <td className="bg-gradient-to-r from-[#1A1000] to-[#1A1200] px-6 py-6 border-r border-[#D4AF37]/20">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 flex items-center justify-center rounded-md bg-[#D4AF37]/15 flex-shrink-0">
                          <i className="ri-trophy-line text-[#D4AF37] text-sm"></i>
                        </div>
                        <span className="text-[#D4AF37] font-bold text-sm tracking-wide">總體定位</span>
                      </div>
                    </td>
                    <td className="bg-gradient-to-r from-[#1A0A0A] to-[#150808] px-6 py-6 border-r border-[#D4AF37]/20 text-center">
                      <div className="flex flex-col items-center gap-1.5">
                        <span className="text-red-400/80 text-xs font-semibold tracking-wide uppercase">零散執行 · 各自為政</span>
                        <span className="text-gray-500 text-xs leading-relaxed">花錢買不到結果，只買到一個網站</span>
                      </div>
                    </td>
                    <td className="bg-gradient-to-r from-[#1A1000] to-[#1A1200] px-6 py-6 text-center relative overflow-hidden">
                      {/* 金色光暈 */}
                      <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/8 via-[#F0C040]/5 to-[#D4AF37]/8 pointer-events-none"></div>
                      <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#D4AF37]/80 via-[#D4AF37]/50 to-[#D4AF37]/20"></div>
                      <div className="relative flex flex-col items-center gap-2">
                        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#F0C040] px-4 py-1.5 rounded-full">
                          <i className="ri-links-line text-[#060F1E] text-xs"></i>
                          <span className="text-[#060F1E] text-xs font-extrabold tracking-widest uppercase whitespace-nowrap">全鏈獲客 = 一站式解決方案</span>
                        </div>
                        <span className="text-[#D4AF37]/80 text-xs leading-relaxed font-medium">視覺 · 漏斗 · 合規 · 數據 · 名單，五位一體</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* ── 分隔線 + 定價標題 ── */}
          <div className="text-center mb-12">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent"></div>
              <span className="text-gray-500 text-xs tracking-widest uppercase px-4">選擇適合您的方案</span>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent"></div>
            </div>
          </div>

          {/* 三欄價格卡片 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-start">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative rounded-2xl p-8 flex flex-col transition-transform duration-300 hover:-translate-y-1 ${
                  plan.highlighted
                    ? 'bg-gradient-to-b from-[#1A1200] to-[#0D1A2A] border-2 border-[#D4AF37] shadow-[0_0_40px_rgba(212,175,55,0.2)]'
                    : 'bg-[#0D1F35] border border-[#1E3A5A]'
                }`}
              >
                {/* 主推標籤 */}
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="inline-block bg-gradient-to-r from-[#D4AF37] to-[#F0C040] text-[#060F1E] text-xs font-bold px-5 py-1.5 rounded-full tracking-wide whitespace-nowrap">
                      {plan.badge}
                    </span>
                  </div>
                )}

                {/* 類別標籤 */}
                <div className="mb-4">
                  <span className={`text-xs font-semibold tracking-widest uppercase ${plan.highlighted ? 'text-[#D4AF37]' : 'text-[#00D4FF]'}`}>
                    {plan.category}
                  </span>
                </div>

                {/* 方案名稱 */}
                <h3 className={`text-xl font-bold mb-6 leading-snug ${plan.highlighted ? 'text-[#F0C040]' : 'text-white'}`}>
                  {plan.name}
                </h3>

                {/* 價格 */}
                <div className={`mb-8 pb-8 border-b ${plan.highlighted ? 'border-[#D4AF37]/30' : 'border-[#1E3A5A]'}`}>
                  <div className={`text-4xl font-extrabold ${plan.highlighted ? 'text-[#F0C040]' : 'text-white'}`}>
                    {plan.price}
                  </div>
                  <div className="text-gray-400 text-sm mt-1">{plan.priceUnit}</div>
                </div>

                {/* 功能列表 */}
                <ul className="space-y-4 flex-1 mb-8">
                  {plan.features.map((feature, fIdx) => (
                    <li key={fIdx} className="flex items-start gap-3">
                      <div className={`mt-0.5 w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 ${plan.highlighted ? 'bg-[#D4AF37]/20' : 'bg-[#00D4FF]/15'}`}>
                        <i className={`ri-check-line text-xs ${plan.highlighted ? 'text-[#D4AF37]' : 'text-[#00D4FF]'}`}></i>
                      </div>
                      <span className={`text-sm leading-relaxed ${feature.bold ? 'text-white font-semibold' : feature.shield ? 'text-[#F0C040] font-medium' : 'text-gray-300'}`}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* 備註 */}
                {plan.note && (
                  <p className="text-xs text-gray-500 leading-relaxed mb-6 border-t border-[#1E3A5A] pt-4">
                    ＊ {plan.note}
                  </p>
                )}

                {/* CTA 按鈕 */}
                <button
                  onClick={handleCTA}
                  className={`w-full py-4 rounded-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    plan.highlighted
                      ? 'bg-gradient-to-r from-[#D4AF37] to-[#F0C040] text-[#060F1E] hover:from-[#E8C547] hover:to-[#FFD700] shadow-[0_4px_20px_rgba(212,175,55,0.45)] hover:shadow-[0_6px_28px_rgba(212,175,55,0.6)]'
                      : 'bg-transparent border border-white/40 text-white hover:bg-white/8 hover:border-white/70'
                  }`}
                >
                  {plan.ctaLabel}
                </button>
              </div>
            ))}
          </div>

          {/* 底部補充說明 */}
          <div className="mt-16 text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-6 bg-[#0D1F35] border border-[#1E3A5A] rounded-2xl px-8 py-6">
              <div className="flex items-center gap-3 text-gray-400 text-sm">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-shield-check-line text-[#00D4FF] text-xl"></i>
                </div>
                <span>所有方案皆提供免費初次評估諮詢</span>
              </div>
              <div className="hidden sm:block w-px h-8 bg-[#1E3A5A]"></div>
              <div className="flex items-center gap-3 text-gray-400 text-sm">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-customer-service-2-line text-[#D4AF37] text-xl"></i>
                </div>
                <span>專屬顧問全程陪跑，不是丟了就跑</span>
              </div>
              <div className="hidden sm:block w-px h-8 bg-[#1E3A5A]"></div>
              <button
                onClick={handleCTA}
                className="bg-gradient-to-r from-[#D4AF37] to-[#F0C040] text-[#060F1E] px-8 py-3 rounded-full font-bold text-sm hover:shadow-[0_4px_20px_rgba(212,175,55,0.5)] transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                預約免費數位診斷
              </button>
            </div>
          </div>
        </div>
      </section>

      <ConsultationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
}
